import React from 'react';
import * as Icons from 'lucide-react';

// Inline data-URI generator for brand tiles when the CDN no longer hosts the
// real logo (Adobe products were removed from simpleicons.org due to trademark).
// Renders a rounded square in the brand colour with the product mark inside.
const brandTile = (label, bg, fg = '#ffffff') => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' rx='12' fill='${bg}'/><text x='32' y='42' font-family='Helvetica,Arial,sans-serif' font-weight='900' font-size='${label.length > 2 ? 18 : 28}' fill='${fg}' text-anchor='middle'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// Real brand SVG icons from CDN (Simple Icons / Devicon). Falls back to a Lucide glyph.
// Each entry: name, color (brand hex), iconUrl (raw SVG from CDN), or fallback Lucide name.
export const SOFTWARE_STACK = [
  { name: 'AutoCAD',     color: '#E51F1F', logo: 'https://cdn.simpleicons.org/autocad/E51F1F' },
  { name: 'Revit',       color: '#0696D7', logo: 'https://cdn.simpleicons.org/autodeskrevit/0696D7' },
  { name: 'Civil 3D',    color: '#0696D7', icon: 'Map' },
  { name: '3ds Max',     color: '#137CBD', logo: brandTile('3ds', '#137CBD') },
  { name: 'SketchUp',    color: '#005F9E', logo: 'https://cdn.simpleicons.org/sketchup/005F9E' },
  { name: 'Tekla',       color: '#1E60A7', icon: 'Building2' },
  { name: 'SP3D',        color: '#0F4C81', icon: 'Factory' },
  { name: 'PV Elite',    color: '#D72631', icon: 'Cylinder' },
  { name: 'ETABS',       color: '#0072B5', icon: 'Layers3' },
  { name: 'STAAD.Pro',   color: '#FF6900', icon: 'BarChart3' },
  { name: 'ArcGIS',      color: '#0099D6', icon: 'Globe' },
  { name: 'QGIS',        color: '#589632', logo: 'https://cdn.simpleicons.org/qgis/589632' },
  { name: 'HEC-RAS',     color: '#0072B5', icon: 'Waves' },
  { name: 'Lumion',      color: '#1E73BE', icon: 'Camera' },
  { name: 'Twinmotion',  color: '#0E1117', icon: 'Film' },
  { name: 'Photoshop',   color: '#31A8FF', logo: brandTile('Ps', '#001E36', '#31A8FF') },
  { name: 'InDesign',    color: '#FF3366', logo: brandTile('Id', '#49021F', '#FF3366') },
  { name: 'Illustrator', color: '#FF9A00', logo: brandTile('Ai', '#330000', '#FF9A00') },
  { name: 'Bluebeam',    color: '#00529B', icon: 'FileText' },
  { name: 'MicroStation',color: '#FFA000', icon: 'PenTool' },
  { name: 'Rhino',       color: '#801010', logo: 'https://cdn.simpleicons.org/rhinoceros/801010' },
  { name: 'BIM 360',     color: '#0696D7', icon: 'Cloud' },
  { name: 'Navisworks',  color: '#0696D7', icon: 'Boxes' },
  { name: 'Inventor',    color: '#0696D7', logo: 'https://cdn.simpleicons.org/autodesk/0696D7' }
];

const SoftwareLogos = ({
  heading = 'Software We Use',
  subheading = 'Fluent in every tool your team already runs',
  light = true
}) => {
  const isLight = light;
  return (
    <section
      data-testid="software-logos"
      className={`relative py-20 overflow-hidden ${isLight ? 'bg-gradient-to-br from-amber-50 via-white to-gray-50 text-[#0e1116]' : 'bg-[#0e1116] text-white'}`}
    >
      <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.10)_1px,transparent_1px)] [background-size:48px_48px] pointer-events-none"></div>
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-orange-500 text-[11px] uppercase tracking-[0.25em] font-bold mb-3">Tooling</div>
          <h2 className="text-3xl md:text-5xl font-black leading-tight">
            {heading.split(' ').slice(0, -2).join(' ')}{' '}
            <span className="text-green-600">{heading.split(' ').slice(-2, -1)}</span>{' '}
            <span className="text-orange-500">{heading.split(' ').slice(-1)}</span>
          </h2>
          <p className={`mt-4 max-w-2xl mx-auto ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>{subheading}</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 md:gap-4">
          {SOFTWARE_STACK.map((s, i) => {
            const Ic = s.icon ? (Icons[s.icon] || Icons.Box) : null;
            return (
              <div
                key={s.name}
                data-testid={`sw-${s.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                className={`group relative rounded-lg p-4 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-1 ${isLight ? 'bg-white border border-gray-200 hover:border-green-500 hover:shadow-xl' : 'bg-[#1a1f2e] border border-gray-900/40 hover:border-green-500'}`}
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center mb-2 transition-transform group-hover:scale-110">
                  {s.logo ? (
                    <img
                      src={s.logo}
                      alt={s.name}
                      className="w-10 h-10 object-contain"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    Ic && <Ic className="w-9 h-9" style={{ color: s.color }} />
                  )}
                </div>
                <div className={`text-[11px] md:text-[12px] font-bold ${isLight ? 'text-gray-800' : 'text-gray-200'} leading-tight`}>{s.name}</div>
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-gray-500 to-orange-500 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SoftwareLogos;
